import {
  Box,
  chakra,
  Checkbox,
  Flex,
  IconButton,
  Image,
  shouldForwardProp,
  Text,
  Spinner,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { COMMON_WEB_PATH } from "../../constant";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValidMotionProp, motion } from "framer-motion";
import ReferQrcode from "../../pages/ReferCode/components/referQrcode";
import { IoClose } from "react-icons/io5";
import ClearCashButTransfer from "../ClearCash/ClearCashButTransfer";
import useWebsiteInfo from "../../hook/useWebsiteInfo";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import useLongMachineSetting from "../../hook/useLongMachineSetting";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const EndGameDetail = ({
  onClose,
  previousBalance,
  transCode,
  withdrawalNumber,
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);

  const [isClosing, setIsClosing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);
  const [capturedImage, setCapturedImage] = useState("");
  const captureRef = useRef(null);

  const memberInfo = useSelector((state) => state.isMemberInfo);
  // 只在組件初始化時設置一次時間
  const [currentTime] = useState(dayjs().format("YYYY-MM-DD HH:mm:ss"));
  const navigate = useNavigate();

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const dispatch = useDispatch();
  const { images, websiteName } = useWebsiteInfo();

  // 用 withdrawalNumber 帶入網址生成 QRcode 掃描後 line 通知棄分資訊
  const lineGiveupQrCodeUrl = useMemo(() => {
    return `https://liff.line.me/2007099270-Gnpl645N?withdrawal_number=${
      withdrawalNumber || ""
    }`;
  }, [withdrawalNumber]);

  // 生成訂單連結 QRCode
  const orderUrl = useMemo(() => {
    return `${window.location.origin}/cash-register/order/${withdrawalNumber}`;
  }, [withdrawalNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (captureRef.current) {
        html2canvas(captureRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: null,
          onclone: (clonedDoc) => {
            // 修正 background-clip: text 不支援的問題
            const fixElements = clonedDoc.querySelectorAll(
              "[data-html2canvas-fix]",
            );
            fixElements.forEach((el) => {
              el.style.background = "none";
              el.style.webkitBackgroundClip = "initial";
              el.style.backgroundClip = "initial";
              el.style.color = "#fdd26a";
            });

            // 修正標題漸層文字
            const titleElement = clonedDoc.querySelector("[data-title-fix]");
            if (titleElement) {
              titleElement.style.color = "white";
              titleElement.style.webkitTextFillColor = "white";
            }
          },
        }).then((canvas) => {
          setCapturedImage(canvas.toDataURL("image/png"));
          setIsGenerating(false);
        });
      }
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // 處理確認按鈕
  const handleConfirm = () => {
    setIsClosing(true);
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        song: "back2",
        musicVolume: 0.2,
      }),
    );
    navigate("/"); // 導航到首頁
    onClose();
  };

  const gradientStyle = {
    color: "white",
    WebkitTextStroke: "1px #804ed2",
    textStroke: "1px #804ed2",
    textShadow: `
    -1px -1px 0 #804ed2,  
     1px -1px 0 #804ed2,
    -1px 1px 0 #804ed2,
     1px 1px 0 #804ed2
  `,
    display: "inline-block",
  };

  const ContentJSX = ({ isCapture = false }) => (
    <Flex
      flexDirection="column"
      gap={2}
      justifyContent="center"
      position="relative"
      zIndex={2}
      w="100%"
    >
      <Flex
        fontSize="24px"
        color="white"
        textAlign="center"
        sx={{ width: "-webkit-fill-available" }}
        justifyContent="center"
        alignItems="center"
        h="70px"
      >
        <Text
          color="transparent"
          sx={gradientStyle}
          fontSize="32px"
          fontWeight="900"
          data-title-fix
        >
          {i18n("BETTING PAYOUT")}
        </Text>
      </Flex>

      <Flex
        justifyContent="center"
        alignItems="center"
        fontSize="36px"
        color="white"
        textAlign="center"
        gap={4}
        h="110px"
      >
        {i18n("machineCode")}
        <Text color="#fff" fontWeight="700">
          {"<NO."}
          {memberInfo?.uid || "-"}
          {">"}
        </Text>
      </Flex>
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          "& > p": {
            background:
              "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
          },
        }}
        fontSize="32px"
        color="white"
        textAlign="center"
        h="240px"
      >
        <Text data-html2canvas-fix>{i18n("GAME RESULT")}</Text>

        <Text
          color="#FC97FF"
          fontWeight="700"
          mt={4}
          fontSize="36px"
          data-html2canvas-fix
        >
          ${previousBalance}
        </Text>
        <Box fontSize="20px" textAlign="center" mt={1}>
          <Text color="#C5B3C7" fontWeight="500">
            {currentTime}
          </Text>
        </Box>
        <Box fontSize="20px" textAlign="center">
          <Flex
            gap={2}
            color="#C5B3C7"
            fontWeight="500"
            justifyContent="center"
          >
            <span>{i18n("withdrawNumber")}:</span>
            {withdrawalNumber || "-"}
          </Flex>
        </Box>
      </Flex>

      <Flex
        gap={0}
        my={2}
        direction={"column"}
        justifyContent="center"
        alignItems="center"
        h="105px"
      >
        {/* <Box p={1} borderRadius={2} bg="white">
          <a className="cursor-pointer" href={lineGiveupQrCodeUrl}>
            <ReferQrcode
              className={{
                w: "80px",
                h: "80px",
              }}
              value={lineGiveupQrCodeUrl}
            />
          </a>
        </Box> */}
        {/* {i18n("lineGiveupQrCodeSCAN")} */}
        <Box bg="white" p={2} borderRadius="md" zIndex={2}>
          <QRCodeCanvas value={orderUrl} size={80} />
        </Box>
        {i18n("orderVerification")}
      </Flex>

      {/* 浮水印 - 僅在擷取時顯示 */}
      {isCapture && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%) rotate(-15deg)"
          opacity={0.05}
          fontSize="40px"
          fontWeight="900"
          fontStyle="italic"
          color="white"
          pointerEvents="none"
          whiteSpace="nowrap"
          zIndex={10}
        >
          {websiteName} - {i18n("棄分")}: {previousBalance}
        </Box>
      )}
    </Flex>
  );

  const containerSx = {
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgImage: `url('${COMMON_WEB_PATH}/911TW/hintBG_big.webp')`,
      bgPosition: "center",
      bgRepeat: "no-repeat",
      bgSize: "100% 100%",
      zIndex: 0,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgImage: `url('${COMMON_WEB_PATH}/911TW/endDetailFrame.gif')`,
      bgPosition: "center",
      bgRepeat: "no-repeat",
      bgSize: "100% 100%",
      zIndex: 1,
      margin: "10px",
    },
  };
  const { longMachinePaddingTop } = useLongMachineSetting();

  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      pt={longMachinePaddingTop}
      bg="rgba(0, 0, 0, 0.8)"
      zIndex="9999"
      backdropFilter="blur(2px)"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
    >
      {/* 1. 底層: 隱藏的擷取區域 */}
      <Box position="absolute" left="-9999px" top="-9999px">
        <Box
          ref={captureRef}
          w="520px"
          h="640px"
          position="relative"
          sx={containerSx}
          p={6}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <ContentJSX isCapture={true} />
        </Box>
      </Box>

      {/* 2. 中層: 顯示區域 */}
      <ChakraBox
        as={Flex}
        w="520px"
        h="640px"
        position={"relative"}
        sx={containerSx}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        p={isGenerating ? 6 : 0}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        gap={2}
      >
        {isGenerating ? (
          <>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="yellow.500"
              size="xl"
              my="auto"
              zIndex={2}
            />
          </>
        ) : (
          <Image
            zIndex={1}
            src={capturedImage}
            w="100%"
            h="auto"
            borderRadius="md"
          />
        )}

        {/* QRcode遮罩 - 保持實體渲染 */}
        <ClearCashButTransfer
          onCancelClearCash={onClose}
          transCode={transCode}
        />
      </ChakraBox>

      {/* 3. 頂層: 交互元素 */}
      <Flex
        as="button"
        w="165px"
        h="62px"
        bgImage={`url('${images.yesBTN}')`}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="100% 100%"
        cursor="pointer"
        justifyContent="center"
        alignItems="center"
        onClick={handleConfirm}
        _hover={{ filter: "brightness(1.1)" }}
        zIndex={3}
        mt={2} // 如果生成後圖片較長，調整按鈕位置
      ></Flex>

      {/* QRCode 位於結算資訊彈窗下方 */}
      {/* {!isGenerating && (
        <Box
          position="absolute"
          left={0}
          bottom={0}
          mt={4}
          p={4}
          bg="white"
          borderRadius="md"
          zIndex={2}
        >
          <QRCodeCanvas value={orderUrl} size={144} />
        </Box>
      )} */}
    </Flex>
  );
};

export default EndGameDetail;
